import { ToggleInput } from "@datagame/ui";
import { ChangeEvent, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { Game, GameVisualsUpdateInput, LibraryImage, LibraryImageKind } from "../../../../../../gql/graphql";
import { ColorInput } from "../../../../../shared/ui/ColorInput";
import { TooltipWithIcon } from "../../../../../shared/ui/TooltipWithIcon";
import { Container, ImageInput, LeftSide, RightSide, Section } from "../Common";
import { ActionTypes, reducer, State } from "./Form/Form";

import styles from "./Form/Form.module.scss";

export interface CustomizeVisualsWordlerProps {
  game: Game;
  onUpdate: (gameVisuals: GameVisualsUpdateInput) => void;
}

export const CustomizeVisualsWordle = ({ game, onUpdate }: CustomizeVisualsWordlerProps) => {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(reducer, game.configuration.gameVisuals as State);
  const [isUsingImage, setIsUsingImage] = useState(Boolean(state.backgroundImage));

  const handleUpdate = () => {
    onUpdate({
      themeColor: state.themeColor,
      backgroundImageId: state.backgroundImage?.id,
      backgroundColor: state.backgroundColor,
      backgroundPositiveColor: state.backgroundPositiveColor,
      backgroundNegativeColor: state.backgroundNegativeColor,
      iconSet: state.iconSet,
      presentLetter: state.presentLetter,
      correctLetter: state.correctLetter,
      defaultLetter: state.defaultLetter,
      absentLetter: state.absentLetter,
    });
  };

  const handleRemoveBackgroundImage = () => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image: null });
    onUpdate({ backgroundImageId: null });
  };

  const handleUploadBackgroundImage = (image: LibraryImage) => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image });
    onUpdate({ backgroundImageId: image.id });
  };

  const handleIsUsingImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    if (!value) {
      handleRemoveBackgroundImage();
    }
    setIsUsingImage(event.target.checked);
  };

  const renderBackgroundSectionContent = () => {
    if (isUsingImage) {
      return (
        <ImageInput
          image={state.backgroundImage}
          kind={LibraryImageKind.Background}
          onUpload={handleUploadBackgroundImage}
          onRemove={handleRemoveBackgroundImage}
          showRemoveButton={true}
        />
      );
    }

    return (
      <div className={styles.backgroundColors}>
        <Section
          title={t("project:customizeVisuals.form.positiveColor")}
          className={styles.sectionWithoutMargin}
          tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.backgroundPositiveColorTooltip")} />}
        >
          <ColorInput
            value={state.backgroundPositiveColor}
            onChange={(color: string) => dispatch({ type: ActionTypes.updateBackgroundPositiveColor, color })}
            onBlur={handleUpdate}
          />
        </Section>
        <Section
          title={t("project:customizeVisuals.form.negativeColor")}
          className={styles.sectionWithoutMargin}
          tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.backgroundNegativeColorTooltip")} />}
        >
          <ColorInput
            value={state.backgroundNegativeColor}
            onChange={(color: string) => dispatch({ type: ActionTypes.updateBackgroundNegativeColor, color })}
            onBlur={handleUpdate}
          />
        </Section>
      </div>
    );
  };

  return (
    <Container>
      <RightSide></RightSide>
      <LeftSide>
        <div className={styles.fieldsWrapper}>
          <Section
            title={t("project:customizeVisuals.form.theme")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.themeTooltip")} />}
          >
            <ColorInput
              value={state.themeColor}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateThemeColor, themeColor: color })}
              onBlur={handleUpdate}
            />
          </Section>
          <Section
            title={t("project:customizeVisuals.form.letterPresentColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.letterPresentColorTooltip")} />}
          >
            <ColorInput
              value={state.presentLetter}
              onChange={(color: string) => dispatch({ type: ActionTypes.updatePresentLetter, color })}
              onBlur={handleUpdate}
            />
          </Section>

          <Section
            title={t("project:customizeVisuals.form.letterCorrectColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.letterCorrectColorTooltip")} />}
          >
            <ColorInput
              value={state.correctLetter}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateCorrectLetter, color })}
              onBlur={handleUpdate}
            />
          </Section>

          <Section
            title={t("project:customizeVisuals.form.letterAbsentColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.letterAbsentColorTooltip")} />}
          >
            <ColorInput
              value={state.absentLetter}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateAbsentLetter, color })}
              onBlur={handleUpdate}
            />
          </Section>

          <Section
            title={t("project:customizeVisuals.form.letterDefaultColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.letterDefaultColorTooltip")} />}
          >
            <ColorInput
              value={state.defaultLetter}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateDefaultLetter, color })}
              onBlur={handleUpdate}
            />
          </Section>

          <Section
            title={t(`project:customizeVisuals.form.${isUsingImage ? "backgroundImage" : "backgroundColor"}`)}
            className={styles.sectionWithoutMargin}
            tooltip={
              <TooltipWithIcon
                content={t(
                  `project:customizeVisuals.form.${isUsingImage ? "backgroundImageTooltip" : "backgroundColorTooltip"}`,
                )}
              />
            }
          >
            {renderBackgroundSectionContent()}
            <ToggleInput
              className={styles.toggle}
              onChange={handleIsUsingImageChange}
              value={isUsingImage}
              label={t("project:gameResults.useImage") ?? ""}
              name="transfer"
              tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.useImageTooltip")} />}
            />
          </Section>
        </div>
      </LeftSide>
    </Container>
  );
};
